import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Sparkles, Cat, TriangleAlert, Crown, ScanSearch } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import { AI_SUGGESTIONS } from '../../mock';
import UpgradeProModal from '../../components/UpgradeProModal';
import api from '../../api';

function getSession() {
  let s = localStorage.getItem('pp_session');
  if (!s) { s = 'sess-' + Math.random().toString(36).slice(2); localStorage.setItem('pp_session', s); }
  return s;
}

export default function AskVet() {
  const nav = useNavigate();
  const { selectedCat, user } = useApp();
  const cat = selectedCat;
  const sid = useRef(getSession());
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState([]);
  const [typing, setTyping] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/ai/chat/${sid.current}`); setMsgs(r.data.messages || []); } catch (e) {}
      try {
        const u = await api.get('/usage');
        setRemaining(u.data.remaining);
        if (!u.data.is_pro && u.data.remaining <= 0) setLimitReached(true);
      } catch (e) {}
    })();
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, typing]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || typing || limitReached) return;
    setMsgs((p) => [...p, { role: 'user', text: msg, ts: Date.now() }]);
    setInput('');
    setTyping(true);
    try {
      const r = await api.post('/ai/chat', { session_id: sid.current, message: msg, cat_name: cat?.name, cat_breed: cat?.breed });
      setMsgs((p) => [...p, { role: 'ai', text: r.data.reply, ts: Date.now() }]);
      setRemaining(r.data.remaining);
      if (r.data.emergency) setEmergency(true);
      if (r.data.remaining !== null && r.data.remaining <= 0) {
        setLimitReached(true);
        if (!user?.is_pro) setShowUpgrade(true);
      }
    } catch (e) {
      setMsgs((p) => p.slice(0, -1));
      if (e?.response?.status === 402) {
        setLimitReached(true);
        setShowUpgrade(true);
      } else {
        toast.error('The AI vet is unavailable right now. Please try again.');
      }
    } finally { setTyping(false); }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent"><Sparkles className="h-5 w-5 text-[hsl(var(--coral))]" /></div>
        <div>
          <h1 className="text-base font-extrabold leading-tight">AI Vet</h1>
          <p className="text-xs text-muted-foreground">
            {remaining === null ? `Here for ${cat?.name} · usually replies instantly` : `${remaining} free question${remaining === 1 ? '' : 's'} left this month`}
          </p>
        </div>
      </header>

      {emergency && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-2xl border border-destructive/40 bg-destructive/10 p-3 animate-pop">
          <TriangleAlert className="h-5 w-5 shrink-0 text-destructive" />
          <div className="flex-1">
            <p className="text-sm font-bold text-destructive">This could be an emergency</p>
            <p className="text-xs text-muted-foreground">If your cat is in distress, contact an emergency vet right away.</p>
            <button onClick={() => nav('/app/vets')} className="mt-2 rounded-full bg-destructive px-3 py-1.5 text-xs font-bold text-white">Find emergency vet</button>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-3 px-4 py-4">
        {msgs.length === 0 && !typing && (
          <div className="animate-fade-up rounded-2xl bg-secondary/60 p-4">
            <div className="flex items-center gap-2 text-[hsl(var(--coral))]"><Cat className="h-5 w-5" /><span className="font-bold">Hi! I’m your AI vet.</span></div>
            <p className="mt-1 text-sm text-muted-foreground">Ask me anything about {cat?.name}’s health, food, or behavior. Try one of these:</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {AI_SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-foreground transition hover:border-[hsl(var(--coral))] hover:text-[hsl(var(--coral))]">{s}</button>
              ))}
            </div>
          </div>
        )}

        {msgs.length === 0 && !typing && (
          <button onClick={() => nav('/app/breed-id')} data-testid="breed-id-entry" className="flex w-full items-center gap-3 rounded-2xl border border-border bg-white p-4 text-left transition hover:border-[hsl(var(--coral))] card-soft">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent"><ScanSearch className="h-5 w-5 text-[hsl(var(--coral))]" /></div>
            <div className="flex-1">
              <p className="font-extrabold">Breed Identifier</p>
              <p className="text-xs text-muted-foreground">Snap a photo to identify your cat's breed — great for mixed breeds.</p>
            </div>
          </button>
        )}

        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`animate-pop max-w-[82%] whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'rounded-br-md bg-[hsl(var(--coral))] text-white' : 'rounded-bl-md bg-secondary text-foreground'}`}>{m.text}</div>
          </div>
        ))}
        {typing && (
          <div className="flex justify-start"><div className="rounded-2xl rounded-bl-md bg-secondary px-4 py-3"><span className="flex gap-1"><i className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{animationDelay:'0ms'}} /><i className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{animationDelay:'150ms'}} /><i className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{animationDelay:'300ms'}} /></span></div></div>
        )}
        <div ref={endRef} />
      </div>

      <UpgradeProModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />

      <div className="sticky bottom-16 border-t border-border bg-background/95 px-3 py-3 backdrop-blur">
        {limitReached && !user?.is_pro ? (
          <button onClick={() => setShowUpgrade(true)} data-testid="askvet-upgrade-btn" className="coral-btn flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-white">
            <Crown className="h-4 w-4" /> Free limit reached — Upgrade for unlimited
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Ask about your cat…" className="h-12 flex-1 rounded-full border border-input bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            <button onClick={() => send()} className="flex h-12 w-12 items-center justify-center rounded-full coral-btn text-white"><Send className="h-5 w-5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}
